import { z } from 'zod';
import {
  listClientsCached,
  listRateOverridesCached,
  listSitesCached,
  listWorkTypesCached,
  putClientCached,
  putJobLocal,
  putSiteCached,
  touchClientUsed,
  touchSiteUsed,
  type CachedClient,
  type CachedRateOverride,
  type CachedSite,
  type CachedWorkType,
  type DurationType,
  type LocalJob,
} from '../db/jobsDb.js';
import {
  createClientRemote,
  createSite,
  linkClientXeroRemote,
  pushClientXeroRemote,
} from '../api/client.js';
import {
  clientNeedsXeroAttention,
  xeroStatusLabel,
} from '../clients/xeroStatus.js';
import {
  isLineKind,
  requiresSite,
  requiresWorkDate,
  showsDuration,
  usesDirectAmount,
  type LineKind,
} from '../domain/lineKind.js';
import {
  RateResolutionError,
  resolveRate,
} from '../../../src/domain/rates.js';
import { formatPenceGBP } from '../../../src/invoicing/money.js';

export type FormHooks = {
  onSaved: (job: LocalJob) => void;
  onError: (message: string) => void;
};

export type FormMode = 'completed' | 'planned';

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function kindOf(t: CachedWorkType | undefined): LineKind {
  return t && isLineKind(t.line_kind) ? t.line_kind : 'day_rate';
}

export async function mountForm(
  root: HTMLElement,
  hooks: FormHooks,
): Promise<{
  refreshLists: () => Promise<void>;
  setWorkDate: (iso: string) => void;
  setMode: (mode: FormMode) => void;
  loadJob: (job: LocalJob) => Promise<void>;
  clearEdit: () => void;
  focus: () => void;
}> {
  let duration: DurationType = 'full_day';
  let customMode: 'hours' | 'amount' | null = null;
  let selectedSiteIds: string[] = [];
  let cachedSites: CachedSite[] = [];
  let cachedTypes: CachedWorkType[] = [];
  let cachedClients: CachedClient[] = [];
  let cachedOverrides: CachedRateOverride[] = [];
  let formMode: FormMode = 'planned';
  /** When set, submit updates this job instead of creating a new one. */
  let editingJob: LocalJob | null = null;

  root.innerHTML = `
    <p id="form-mode-hint" class="hint" hidden></p>
    <form id="job-form" novalidate>
      <label class="field">
        <span>Work type</span>
        <select name="work_type_id" id="work_type_id" required></select>
      </label>
      <div class="field-block" id="client-field">
        <label class="field">
          <span>Client</span>
          <select name="client_id" id="client_id" required></select>
        </label>
        <button type="button" class="client-add-toggle" id="client-add-toggle">Add new client</button>
        <p class="hint client-xero-hint" id="client-xero-hint" hidden></p>
        <div class="client-xero-actions" id="client-xero-actions" hidden></div>
        <div class="client-new" id="client-new" hidden>
          <p class="client-new-title">New client</p>
          <label class="field">
            <span>Name</span>
            <input type="text" id="new-client-name" autocomplete="organization" required />
          </label>
          <div class="client-new-rates">
            <label class="field">
              <span>Day rate £</span>
              <input type="number" id="new-client-day" min="0" step="0.01" inputmode="decimal" required />
            </label>
            <label class="field">
              <span>Half-day rate £</span>
              <input type="number" id="new-client-half" min="0" step="0.01" inputmode="decimal" required />
            </label>
          </div>
          <p class="hint">Half-day and day rates are stored separately — one is never derived from the other.</p>
          <label class="field">
            <span>Contact name <span class="muted">(optional)</span></span>
            <input type="text" id="new-client-contact" autocomplete="name" />
          </label>
          <label class="field">
            <span>Email <span class="muted">(optional)</span></span>
            <input type="email" id="new-client-email" autocomplete="email" />
          </label>
          <label class="field">
            <span>Address <span class="muted">(optional)</span></span>
            <textarea id="new-client-address" rows="2"></textarea>
          </label>
          <label class="field">
            <span>Payment terms (days)</span>
            <input type="number" id="new-client-terms" min="0" max="365" step="1" inputmode="numeric" value="30" />
          </label>
          <p class="hint">Tax treatment defaults to standard rated.</p>
          <div class="client-new-actions">
            <button type="button" class="submit" id="client-new-save">Save client</button>
            <button type="button" class="btn-secondary" id="client-new-cancel">Cancel</button>
          </div>
        </div>
      </div>
      <label class="field" id="date-field">
        <span>Date</span>
        <input type="date" name="work_date" id="work_date" required />
      </label>
      <label class="field" id="sites-field">
        <span>Sites</span>
        <input type="search" id="site-search" placeholder="Type to find or create a site" autocomplete="off" enterkeyhint="search" role="combobox" aria-autocomplete="list" aria-controls="site-multi" aria-expanded="true" />
        <div class="site-picker" id="site-multi" role="listbox" aria-label="Sites"></div>
        <p class="hint" id="sites-hint">Select one or more. Order is invoice narrative order.</p>
      </label>
      <label class="field" id="description-field" hidden>
        <span>Description</span>
        <textarea name="description" id="description" rows="3" required></textarea>
      </label>
      <label class="field" id="direct-amount-field" hidden>
        <span>Amount £</span>
        <input type="number" name="direct_amount" id="direct_amount" min="0" step="0.01" inputmode="decimal" />
      </label>
      <label class="field check-field" id="billable-field">
        <input type="checkbox" id="billable" checked />
        <span>Billable (untick for non-billable survey days)</span>
      </label>
      <label class="field" id="duration-field">
        <span>Duration</span>
        <div class="duration" role="group" aria-label="Duration">
          <button type="button" data-duration="full_day" aria-pressed="true">Full day</button>
          <button type="button" data-duration="half_day" aria-pressed="false">Half day</button>
          <button type="button" data-duration="custom" aria-pressed="false">Custom</button>
        </div>
        <p class="rate-hint" id="rate-hint" aria-live="polite"></p>
      </label>
      <div class="custom-box" id="custom-box" hidden>
        <div class="custom-toggle">
          <button type="button" data-custom="hours" aria-pressed="false">Hours</button>
          <button type="button" data-custom="amount" aria-pressed="false">Amount £</button>
        </div>
        <label class="field" id="hours-field" hidden>
          <span>Hours</span>
          <input type="number" name="custom_hours" id="custom_hours" min="0.25" step="0.25" inputmode="decimal" />
        </label>
        <label class="field" id="amount-field" hidden>
          <span>Amount</span>
          <input type="number" name="custom_amount" id="custom_amount" min="0" step="0.01" inputmode="decimal" />
        </label>
      </div>
      <details class="more" id="more-details">
        <summary>One-off site / PO / notes</summary>
        <label class="field">
          <span>Site reference (fallback if not in list)</span>
          <input type="text" name="site_reference" id="site_reference" autocomplete="off" />
        </label>
        <label class="field">
          <span>PO number</span>
          <input type="text" name="po_number" id="po_number" autocomplete="off" />
        </label>
        <label class="field">
          <span>Notes</span>
          <textarea name="notes" id="notes" rows="3"></textarea>
        </label>
      </details>
      <button class="submit" type="submit" id="job-submit">Log job</button>
      <div class="flash" id="flash" aria-live="polite"></div>
    </form>
  `;

  const form = root.querySelector('#job-form') as HTMLFormElement;
  const submitBtn = root.querySelector('#job-submit') as HTMLButtonElement;
  const clientSelect = root.querySelector('#client_id') as HTMLSelectElement;
  const workTypeSelect = root.querySelector('#work_type_id') as HTMLSelectElement;
  const dateInput = root.querySelector('#work_date') as HTMLInputElement;
  const siteMulti = root.querySelector('#site-multi') as HTMLElement;
  const billableInput = root.querySelector('#billable') as HTMLInputElement;
  const customBox = root.querySelector('#custom-box') as HTMLElement;
  const hoursField = root.querySelector('#hours-field') as HTMLElement;
  const amountField = root.querySelector('#amount-field') as HTMLElement;
  const hoursInput = root.querySelector('#custom_hours') as HTMLInputElement;
  const amountInput = root.querySelector('#custom_amount') as HTMLInputElement;
  const descriptionInput = root.querySelector('#description') as HTMLTextAreaElement;
  const directAmountInput = root.querySelector('#direct_amount') as HTMLInputElement;
  const flash = root.querySelector('#flash') as HTMLElement;

  const dateField = root.querySelector('#date-field') as HTMLElement;
  const sitesField = root.querySelector('#sites-field') as HTMLElement;
  const durationField = root.querySelector('#duration-field') as HTMLElement;
  const rateHint = root.querySelector('#rate-hint') as HTMLElement;
  const descriptionField = root.querySelector('#description-field') as HTMLElement;
  const directAmountField = root.querySelector('#direct-amount-field') as HTMLElement;
  const billableField = root.querySelector('#billable-field') as HTMLElement;
  const moreDetails = root.querySelector('#more-details') as HTMLElement;
  const clientField = root.querySelector('#client-field') as HTMLElement;
  const clientAddToggle = root.querySelector('#client-add-toggle') as HTMLButtonElement;
  const clientNew = root.querySelector('#client-new') as HTMLElement;
  const clientXeroHint = root.querySelector('#client-xero-hint') as HTMLElement;
  const clientXeroActions = root.querySelector('#client-xero-actions') as HTMLElement;
  const siteSearch = root.querySelector('#site-search') as HTMLInputElement;
  const sitesHint = root.querySelector('#sites-hint') as HTMLElement;

  dateInput.value = todayISO();

  function updateSitesHint() {
    if (formMode === 'planned') {
      sitesHint.textContent =
        'Optional until you confirm the day. Type to find an existing site or create one.';
    } else {
      sitesHint.textContent =
        'Required for completed work. Type to find an existing site or create one. Order is invoice narrative order.';
    }
  }

  function currentKind(): LineKind {
    const t = cachedTypes.find((x) => x.id === workTypeSelect.value);
    return kindOf(t);
  }

  function applyKindUi() {
    const kind = currentKind();
    const showDur = showsDuration(kind);
    const showDate = requiresWorkDate(kind);
    const showSites = requiresSite(kind);
    const direct = usesDirectAmount(kind);
    const expense = kind === 'expense';

    durationField.hidden = !showDur;
    dateField.hidden = !showDate;
    sitesField.hidden = !showSites;
    descriptionField.hidden = !expense;
    directAmountField.hidden = !direct;
    // Expense/custom: client + description + amount only
    billableField.hidden = expense;
    moreDetails.hidden = expense;
    clientField.hidden = false;

    dateInput.required = showDate;
    descriptionInput.required = expense;
    directAmountInput.required = direct;

    if (!showDur) {
      setDuration('custom');
      customBox.hidden = true;
    } else if (duration === 'custom') {
      customBox.hidden = false;
    } else {
      customBox.hidden = true;
    }

    if (expense) {
      billableInput.checked = true;
    }
    updateRateHint();
  }

  /** Display-only preview via the same resolveRate used at invoice time. */
  function updateRateHint() {
    const kind = currentKind();
    if (!showsDuration(kind) || duration === 'custom') {
      rateHint.hidden = true;
      rateHint.textContent = '';
      return;
    }
    rateHint.hidden = false;
    const client = cachedClients.find((c) => c.id === clientSelect.value);
    if (!client) {
      rateHint.textContent = 'Pick a client to see the rate.';
      rateHint.classList.add('is-missing');
      return;
    }
    const overrideRow = cachedOverrides.find(
      (o) =>
        o.client_id === client.id && o.work_type_id === workTypeSelect.value,
    );
    const override = overrideRow
      ? {
          day_rate: overrideRow.day_rate,
          half_day_rate: overrideRow.half_day_rate,
          hourly_rate: overrideRow.hourly_rate,
        }
      : null;
    try {
      const { amountPence } = resolveRate(
        {
          duration_type: duration,
          custom_hours: null,
          custom_amount: null,
        },
        {
          day_rate: client.day_rate ?? '',
          half_day_rate: client.half_day_rate ?? '',
          hourly_rate: client.hourly_rate,
        },
        override,
      );
      if (amountPence <= 0) {
        rateHint.textContent =
          duration === 'half_day'
            ? 'No half-day rate configured for this client'
            : 'No day rate configured for this client';
        rateHint.classList.add('is-missing');
        return;
      }
      const label = duration === 'half_day' ? 'Half day' : 'Full day';
      rateHint.textContent = `${label} · ${formatPenceGBP(amountPence)}`;
      rateHint.classList.remove('is-missing');
    } catch (err) {
      const msg =
        err instanceof RateResolutionError
          ? err.message
          : duration === 'half_day'
            ? 'No half-day rate configured for this client'
            : 'No day rate configured for this client';
      rateHint.textContent = msg;
      rateHint.classList.add('is-missing');
    }
  }

  function setDuration(next: DurationType) {
    duration = next;
    root.querySelectorAll<HTMLButtonElement>('[data-duration]').forEach((btn) => {
      btn.setAttribute(
        'aria-pressed',
        String(btn.dataset.duration === next),
      );
    });
    const showCustom = showsDuration(currentKind()) && next === 'custom';
    customBox.hidden = !showCustom;
    if (!showCustom) {
      if (showsDuration(currentKind())) {
        customMode = null;
        hoursInput.value = '';
        amountInput.value = '';
        hoursField.hidden = true;
        amountField.hidden = true;
        root.querySelectorAll<HTMLButtonElement>('[data-custom]').forEach((btn) => {
          btn.setAttribute('aria-pressed', 'false');
        });
      }
    } else if (!customMode) {
      setCustomMode('hours');
    }
    updateRateHint();
  }

  function setCustomMode(mode: 'hours' | 'amount') {
    customMode = mode;
    root.querySelectorAll<HTMLButtonElement>('[data-custom]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.custom === mode));
    });
    hoursField.hidden = mode !== 'hours';
    amountField.hidden = mode !== 'amount';
    if (mode === 'hours') amountInput.value = '';
    if (mode === 'amount') hoursInput.value = '';
  }

  type SiteSection = { label: string; sites: CachedSite[] };

  function siteSectionsFor(
    available: CachedSite[],
    query: string,
  ): SiteSection[] {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? available.filter((s) => s.name.toLowerCase().includes(needle))
      : available;

    const selected = selectedSiteIds
      .map((id) => filtered.find((s) => s.id === id))
      .filter((s): s is CachedSite => Boolean(s));

    const unselected = filtered.filter((s) => !selectedSiteIds.includes(s.id));

    const recent = [...unselected]
      .filter((s) => s.lastUsedAt)
      .sort((a, b) => (b.lastUsedAt ?? '').localeCompare(a.lastUsedAt ?? ''))
      .slice(0, 5);
    const recentIds = new Set(recent.map((s) => s.id));

    const frequent = [...unselected]
      .filter((s) => !recentIds.has(s.id) && (s.useCount ?? 0) > 0)
      .sort(
        (a, b) =>
          (b.useCount ?? 0) - (a.useCount ?? 0) ||
          a.name.localeCompare(b.name),
      )
      .slice(0, 5);
    const frequentIds = new Set(frequent.map((s) => s.id));

    const alpha = unselected
      .filter((s) => !recentIds.has(s.id) && !frequentIds.has(s.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    const sections: SiteSection[] = [];
    if (selected.length) sections.push({ label: 'Selected', sites: selected });
    if (!needle && recent.length) {
      sections.push({ label: 'Recent', sites: recent });
    }
    if (!needle && frequent.length) {
      sections.push({ label: 'Most used', sites: frequent });
    }
    if (needle) {
      sections.push({ label: 'Matches', sites: [...unselected].sort((a, b) => a.name.localeCompare(b.name)) });
    } else if (alpha.length) {
      sections.push({ label: 'All sites', sites: alpha });
    }
    return sections;
  }

  function renderSites() {
    const clientId = clientSelect.value;
    const available = cachedSites.filter(
      (s) => !clientId || s.client_id === null || s.client_id === clientId,
    );
    selectedSiteIds = selectedSiteIds.filter((id) =>
      available.some((s) => s.id === id) ||
      cachedSites.some((s) => s.id === id),
    );

    if (cachedSites.length === 0 && !siteSearch.value.trim()) {
      siteSearch.hidden = false;
      siteMulti.innerHTML =
        '<p class="meta">No sites synced yet. Type a name to create one (online), or use site reference below for a one-off.</p>';
      return;
    }

    siteSearch.hidden = false;
    const query = siteSearch.value.trim();
    const needle = query.toLowerCase();
    const sections = siteSectionsFor(available, siteSearch.value);
    const exactMatch = available.some(
      (s) => s.name.toLowerCase() === needle,
    );
    const canCreate =
      needle.length > 0 &&
      !exactMatch &&
      !available.some((s) => s.name.toLowerCase() === needle);

    let html: string;
    if (sections.every((s) => s.sites.length === 0) && !canCreate) {
      html = '<p class="meta">No sites match that search.</p>';
    } else {
      html = sections
        .map((sec) => {
          const chips = sec.sites
            .map((s) => {
              const on = selectedSiteIds.includes(s.id);
              const order = selectedSiteIds.indexOf(s.id);
              return `<button type="button" class="site-chip${on ? ' on' : ''}" data-site="${s.id}" role="option" aria-selected="${on}">
              ${escapeHtml(s.name)}${on && selectedSiteIds.length > 1 ? ` <span class="ord">${order + 1}</span>` : ''}
            </button>`;
            })
            .join('');
          return `<div class="site-section"><p class="site-section-label">${escapeHtml(sec.label)}</p><div class="site-multi">${chips}</div></div>`;
        })
        .join('');
    }

    if (canCreate) {
      html += `<div class="site-section"><p class="site-section-label">New</p>
        <button type="button" class="site-chip site-create" data-create-site="${escapeHtml(query)}">
          Create site “${escapeHtml(query)}”
        </button>
        <p class="meta">Creates a real site linked to this client so invoice names stay consistent.</p>
      </div>`;
    }

    siteMulti.innerHTML = html;

    siteMulti.querySelectorAll<HTMLButtonElement>('[data-site]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.site!;
        if (selectedSiteIds.includes(id)) {
          selectedSiteIds = selectedSiteIds.filter((x) => x !== id);
        } else {
          selectedSiteIds = [...selectedSiteIds, id];
          void touchSiteUsed(id).then(() => {
            const row = cachedSites.find((s) => s.id === id);
            if (row) {
              row.lastUsedAt = new Date().toISOString();
              row.useCount = (row.useCount ?? 0) + 1;
            }
          });
        }
        siteSearch.value = '';
        renderSites();
      });
    });

    siteMulti
      .querySelector<HTMLButtonElement>('[data-create-site]')
      ?.addEventListener('click', () => {
        void (async () => {
          const name = (
            siteMulti.querySelector('[data-create-site]') as HTMLButtonElement
          ).dataset.createSite!.trim();
          if (!name) return;
          // Prefer existing case-insensitive match (avoids Long Bennington duplicates)
          const existing = cachedSites.find(
            (s) => s.name.toLowerCase() === name.toLowerCase(),
          );
          if (existing) {
            if (!selectedSiteIds.includes(existing.id)) {
              selectedSiteIds = [...selectedSiteIds, existing.id];
            }
            siteSearch.value = '';
            renderSites();
            return;
          }
          flash.textContent = '';
          flash.classList.remove('error');
          try {
            const created = await createSite({
              name,
              client_id: clientSelect.value || null,
            });
            const cached: CachedSite = {
              id: created.id,
              name: created.name,
              client_id: created.client_id,
              useCount: created.use_count ?? 0,
              lastUsedAt: new Date().toISOString(),
            };
            await putSiteCached(cached);
            cachedSites = [...cachedSites, cached];
            selectedSiteIds = [...selectedSiteIds, cached.id];
            siteSearch.value = '';
            renderSites();
          } catch (err) {
            flash.textContent =
              err instanceof Error
                ? err.message
                : 'Could not create site — unlock online, or use site reference for a one-off.';
            flash.classList.add('error');
          }
        })();
      });
  }

  root.querySelectorAll<HTMLButtonElement>('[data-duration]').forEach((btn) => {
    btn.addEventListener('click', () => {
      setDuration(btn.dataset.duration as DurationType);
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-custom]').forEach((btn) => {
    btn.addEventListener('click', () => {
      setCustomMode(btn.dataset.custom as 'hours' | 'amount');
    });
  });
  function selectedClient(): CachedClient | undefined {
    return cachedClients.find((c) => c.id === clientSelect.value);
  }

  function applyRemoteToCache(remote: {
    id: string;
    name: string;
    day_rate: number | string;
    half_day_rate: number | string;
    hourly_rate: number | string | null;
    contact_name?: string | null;
    contact_email?: string | null;
    address?: string | null;
    payment_terms_days?: number | null;
    external_contact_id?: string | null;
    xero_contact_status?: CachedClient['xero_contact_status'];
    xero_contact_error?: string | null;
    xero_contact_matches?: CachedClient['xero_contact_matches'];
  }): CachedClient {
    const prev = cachedClients.find((c) => c.id === remote.id);
    const next: CachedClient = {
      id: remote.id,
      name: remote.name,
      day_rate: remote.day_rate ?? null,
      half_day_rate: remote.half_day_rate ?? null,
      hourly_rate: remote.hourly_rate ?? null,
      contact_name: remote.contact_name ?? null,
      contact_email: remote.contact_email ?? null,
      address: remote.address ?? null,
      payment_terms_days: remote.payment_terms_days ?? 30,
      external_contact_id: remote.external_contact_id ?? null,
      xero_contact_status: remote.xero_contact_status,
      xero_contact_error: remote.xero_contact_error ?? null,
      xero_contact_matches: remote.xero_contact_matches ?? null,
      pendingOp: null,
      lastUsedAt: prev?.lastUsedAt ?? new Date().toISOString(),
    };
    cachedClients = [next, ...cachedClients.filter((c) => c.id !== next.id)];
    return next;
  }

  function renderClientXeroHint() {
    const client = selectedClient();
    clientXeroActions.innerHTML = '';
    if (!client || !clientNeedsXeroAttention(client)) {
      clientXeroHint.hidden = true;
      clientXeroActions.hidden = true;
      return;
    }
    clientXeroHint.hidden = false;
    clientXeroHint.textContent =
      client.xero_contact_error || xeroStatusLabel(client.xero_contact_status);
    const bits: string[] = [];
    if (client.xero_contact_status !== 'mapped') {
      bits.push(
        `<button type="button" class="btn-secondary" data-xero-retry>Retry Xero</button>`,
      );
    }
    for (const match of client.xero_contact_matches ?? []) {
      bits.push(
        `<button type="button" class="btn-secondary" data-xero-link="${escapeHtml(match.ContactID)}">Link “${escapeHtml(match.Name)}”</button>`,
      );
    }
    clientXeroActions.innerHTML = bits.join('');
    clientXeroActions.hidden = bits.length === 0;
    clientXeroActions
      .querySelector('[data-xero-retry]')
      ?.addEventListener('click', () => {
        void (async () => {
          try {
            const remote = await pushClientXeroRemote(client.id);
            const next = applyRemoteToCache(remote);
            await putClientCached(next);
            renderClientXeroHint();
          } catch (err) {
            flash.textContent =
              err instanceof Error ? err.message : 'Could not reach Xero';
            flash.classList.add('error');
          }
        })();
      });
    clientXeroActions.querySelectorAll('[data-xero-link]').forEach((el) => {
      el.addEventListener('click', () => {
        const contactId = (el as HTMLElement).dataset.xeroLink!;
        void (async () => {
          try {
            const remote = await linkClientXeroRemote(client.id, contactId);
            const next = applyRemoteToCache(remote);
            await putClientCached(next);
            renderClientXeroHint();
          } catch (err) {
            flash.textContent =
              err instanceof Error ? err.message : 'Could not link Xero contact';
            flash.classList.add('error');
          }
        })();
      });
    });
  }

  function setClientNewOpen(open: boolean) {
    clientNew.hidden = !open;
    clientAddToggle.setAttribute('aria-expanded', String(open));
    clientAddToggle.textContent = open ? 'Close new client' : 'Add new client';
  }

  clientNew.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    (root.querySelector('#client-new-save') as HTMLButtonElement).click();
  });

  clientAddToggle.addEventListener('click', () => {
    setClientNewOpen(clientNew.hidden);
    if (!clientNew.hidden) {
      (root.querySelector('#new-client-name') as HTMLInputElement).focus();
    }
  });
  root.querySelector('#client-new-cancel')?.addEventListener('click', () => {
    setClientNewOpen(false);
  });
  root.querySelector('#client-new-save')?.addEventListener('click', () => {
    void (async () => {
      const name = (root.querySelector('#new-client-name') as HTMLInputElement)
        .value.trim();
      const dayRaw = (root.querySelector('#new-client-day') as HTMLInputElement)
        .value.trim();
      const halfRaw = (root.querySelector('#new-client-half') as HTMLInputElement)
        .value.trim();
      const contact = (
        root.querySelector('#new-client-contact') as HTMLInputElement
      ).value.trim();
      const email = (
        root.querySelector('#new-client-email') as HTMLInputElement
      ).value.trim();
      const address = (
        root.querySelector('#new-client-address') as HTMLTextAreaElement
      ).value.trim();
      const termsRaw = (
        root.querySelector('#new-client-terms') as HTMLInputElement
      ).value.trim();
      flash.textContent = '';
      flash.classList.remove('error');
      if (!name) {
        flash.textContent = 'Enter a client name';
        flash.classList.add('error');
        return;
      }
      const dayRate = Number(dayRaw);
      const halfRate = Number(halfRaw);
      if (!Number.isFinite(dayRate) || dayRate < 0 || !dayRaw) {
        flash.textContent = 'Enter a day rate';
        flash.classList.add('error');
        return;
      }
      if (!Number.isFinite(halfRate) || halfRate < 0 || !halfRaw) {
        flash.textContent = 'Enter a half-day rate';
        flash.classList.add('error');
        return;
      }
      const terms = termsRaw ? Number(termsRaw) : 30;
      const id = crypto.randomUUID();
      const local: CachedClient = {
        id,
        name,
        day_rate: dayRate,
        half_day_rate: halfRate,
        hourly_rate: null,
        contact_name: contact || null,
        contact_email: email || null,
        address: address || null,
        payment_terms_days: Number.isFinite(terms) ? terms : 30,
        external_contact_id: null,
        xero_contact_status: 'pending',
        xero_contact_error: null,
        xero_contact_matches: null,
        pendingOp: 'create',
        lastUsedAt: new Date().toISOString(),
      };
      await putClientCached(local);
      cachedClients = [local, ...cachedClients.filter((c) => c.id !== id)];
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = name;
      clientSelect.append(opt);
      clientSelect.value = id;
      setClientNewOpen(false);
      (
        [
          '#new-client-name',
          '#new-client-day',
          '#new-client-half',
          '#new-client-contact',
          '#new-client-email',
        ] as const
      ).forEach((sel) => {
        (root.querySelector(sel) as HTMLInputElement).value = '';
      });
      (root.querySelector('#new-client-address') as HTMLTextAreaElement).value =
        '';
      (root.querySelector('#new-client-terms') as HTMLInputElement).value = '30';
      try {
        const remote = await createClientRemote({
          id,
          name,
          day_rate: dayRate,
          half_day_rate: halfRate,
          contact_name: contact || null,
          contact_email: email || null,
          address: address || null,
          payment_terms_days: Number.isFinite(terms) ? terms : 30,
        });
        const next = applyRemoteToCache(remote);
        await putClientCached(next);
      } catch (err) {
        // Offline or auth — local row stays queued for flush.
        console.error(
          'createClientRemote failed; client stays queued',
          err,
        );
      }
      renderClientXeroHint();
      updateRateHint();
      void renderSites();
    })();
  });

  clientSelect.addEventListener('change', () => {
    siteSearch.value = '';
    void renderSites();
    updateRateHint();
    renderClientXeroHint();
  });
  workTypeSelect.addEventListener('change', () => {
    applyKindUi();
  });
  siteSearch.addEventListener('input', () => {
    renderSites();
  });
  // Enter in a search field inside <form> would submit and reload the page.
  siteSearch.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    const createBtn = siteMulti.querySelector(
      '[data-create-site]',
    ) as HTMLButtonElement | null;
    const firstSite = siteMulti.querySelector(
      '[data-site]',
    ) as HTMLButtonElement | null;
    if (createBtn) createBtn.click();
    else if (firstSite) firstSite.click();
  });

  async function refreshLists() {
    cachedClients = await listClientsCached();
    cachedTypes = await listWorkTypesCached();
    cachedSites = await listSitesCached();
    cachedOverrides = await listRateOverridesCached();
    const clients = cachedClients;
    const prevClient = clientSelect.value;
    const prevType = workTypeSelect.value;

    clientSelect.innerHTML = clients.length
      ? clients
          .map((c) => {
            const mark = clientNeedsXeroAttention(c) ? ' · not in Xero' : '';
            return `<option value="${c.id}">${escapeHtml(c.name)}${mark}</option>`;
          })
          .join('')
      : '<option value="">No clients cached — sign in when online</option>';

    workTypeSelect.innerHTML = cachedTypes.length
      ? cachedTypes
          .map(
            (t) =>
              `<option value="${t.id}">${escapeHtml(t.short_description)}</option>`,
          )
          .join('')
      : '<option value="">No work types cached — sign in when online</option>';

    if (prevClient && clients.some((c) => c.id === prevClient)) {
      clientSelect.value = prevClient;
    }
    if (prevType && cachedTypes.some((t) => t.id === prevType)) {
      workTypeSelect.value = prevType;
    }
    renderSites();
    applyKindUi();
    renderClientXeroHint();
  }

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    flash.textContent = '';
    flash.classList.remove('error');

    const kind = currentKind();
    const hoursRaw = hoursInput.value.trim();
    const amountRaw = amountInput.value.trim();
    const directRaw = directAmountInput.value.trim();
    const siteRef =
      (root.querySelector('#site_reference') as HTMLInputElement).value.trim() ||
      null;
    const notesExtra =
      (root.querySelector('#notes') as HTMLTextAreaElement).value.trim() || null;
    const description = descriptionInput.value.trim();

    if (
      formMode === 'completed' &&
      requiresSite(kind) &&
      selectedSiteIds.length === 0 &&
      !siteRef
    ) {
      flash.textContent = 'Select a site or enter a site reference';
      flash.classList.add('error');
      hooks.onError(flash.textContent);
      return;
    }

    let durationType: DurationType = duration;
    let customHours: number | null = null;
    let customAmount: number | null = null;
    let notes: string | null = notesExtra;

    if (usesDirectAmount(kind)) {
      durationType = 'custom';
      if (!directRaw) {
        flash.textContent = 'Enter an amount';
        flash.classList.add('error');
        hooks.onError(flash.textContent);
        return;
      }
      customAmount = Number(directRaw);
      if (!Number.isFinite(customAmount) || customAmount < 0) {
        flash.textContent = 'Enter a valid amount';
        flash.classList.add('error');
        hooks.onError(flash.textContent);
        return;
      }
      if (kind === 'expense') {
        if (!description) {
          flash.textContent = 'Enter a description';
          flash.classList.add('error');
          hooks.onError(flash.textContent);
          return;
        }
        notes = description;
      }
    } else if (durationType === 'custom') {
      if (customMode === 'hours' && hoursRaw) customHours = Number(hoursRaw);
      if (customMode === 'amount' && amountRaw) customAmount = Number(amountRaw);
      if (customMode === 'hours' && customHours == null) {
        flash.textContent = 'Enter hours';
        flash.classList.add('error');
        hooks.onError(flash.textContent);
        return;
      }
      if (customMode === 'amount' && customAmount == null) {
        flash.textContent = 'Enter amount';
        flash.classList.add('error');
        hooks.onError(flash.textContent);
        return;
      }
      if (!customMode) {
        flash.textContent = 'Choose hours or amount';
        flash.classList.add('error');
        hooks.onError(flash.textContent);
        return;
      }
    }

    const workDate = requiresWorkDate(kind) ? dateInput.value : todayISO();
    const parsed = z
      .object({
        work_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        client_id: z.string().uuid(),
        work_type_id: z.string().uuid(),
      })
      .safeParse({
        work_date: workDate,
        client_id: clientSelect.value,
        work_type_id: workTypeSelect.value,
      });

    if (!parsed.success) {
      flash.textContent = parsed.error.issues[0]?.message ?? 'Check the form';
      flash.classList.add('error');
      hooks.onError(flash.textContent);
      return;
    }

    const now = new Date().toISOString();
    if (editingJob?.invoice_run_id) {
      flash.textContent = `Invoiced on ${editingJob.invoice_number ?? 'invoice'} — cannot be edited`;
      flash.classList.add('error');
      hooks.onError(flash.textContent);
      return;
    }
    if (editingJob?.released_at) {
      flash.textContent =
        'Released for the Saturday run — un-release the week first';
      flash.classList.add('error');
      hooks.onError(flash.textContent);
      return;
    }

    const isEdit = Boolean(editingJob);
    const status: FormMode | 'cancelled' = isEdit
      ? editingJob!.status === 'cancelled'
        ? 'cancelled'
        : formMode
      : formMode;
    const pendingOp = isEdit
      ? editingJob!.pendingOp === 'create'
        ? 'create'
        : 'update'
      : 'create';

    const job: LocalJob = {
      id: editingJob?.id ?? crypto.randomUUID(),
      work_date: parsed.data.work_date,
      client_id: parsed.data.client_id,
      work_type_id: parsed.data.work_type_id,
      duration_type: durationType,
      custom_hours: customHours,
      custom_amount: customAmount,
      site_ids: usesDirectAmount(kind) && kind === 'expense' ? [] : selectedSiteIds,
      site_reference: kind === 'expense' ? null : siteRef,
      billable: kind === 'expense' ? true : billableInput.checked,
      status,
      start_time: editingJob?.start_time ?? null,
      confirmed_at:
        status === 'completed'
          ? editingJob?.confirmed_at ?? now
          : editingJob?.confirmed_at ?? null,
      released_at: editingJob?.released_at ?? null,
      xero_invoice_id: editingJob?.xero_invoice_id ?? null,
      po_number:
        kind === 'expense'
          ? null
          : (root.querySelector('#po_number') as HTMLInputElement).value.trim() ||
            null,
      notes,
      syncState: 'pending',
      failReason: null,
      invoice_run_id: editingJob?.invoice_run_id ?? null,
      invoice_number: editingJob?.invoice_number ?? null,
      deleted_at: null,
      pendingOp,
      createdAt: editingJob?.createdAt ?? now,
      updatedAt: now,
      lastSyncAttemptAt: editingJob?.lastSyncAttemptAt ?? null,
    };

    try {
      await putJobLocal(job);
      await touchClientUsed(job.client_id);
      flash.textContent = isEdit
        ? 'Changes saved on this phone'
        : formMode === 'planned'
          ? 'Booking saved on this phone'
          : job.billable
            ? 'Saved on this phone'
            : 'Saved (non-billable)';
      editingJob = null;
      submitBtn.textContent = 'Log job';
      hooks.onSaved(job);
      formMode = 'planned';
      const modeHint = root.querySelector('#form-mode-hint') as HTMLElement;
      modeHint.hidden = true;
      (root.querySelector('#notes') as HTMLTextAreaElement).value = '';
      (root.querySelector('#site_reference') as HTMLInputElement).value = '';
      (root.querySelector('#po_number') as HTMLInputElement).value = '';
      descriptionInput.value = '';
      directAmountInput.value = '';
      hoursInput.value = '';
      amountInput.value = '';
      selectedSiteIds = [];
      billableInput.checked = true;
      setDuration('full_day');
      applyKindUi();
      renderSites();
    } catch (err) {
      flash.textContent =
        err instanceof Error ? err.message : 'Could not save locally';
      flash.classList.add('error');
      hooks.onError(flash.textContent);
    }
  });

  async function loadJob(job: LocalJob) {
    editingJob = job;
    submitBtn.textContent = 'Save changes';
    formMode = job.status === 'planned' ? 'planned' : 'completed';
    const modeHint = root.querySelector('#form-mode-hint') as HTMLElement;
    if (job.invoice_run_id) {
      modeHint.hidden = false;
      modeHint.textContent = `Invoiced${job.invoice_number ? ` #${job.invoice_number}` : ''} — read only.`;
    } else if (job.released_at) {
      modeHint.hidden = false;
      modeHint.textContent =
        'Released for Saturday’s invoice run — un-release the week to edit.';
    } else if (formMode === 'planned') {
      modeHint.hidden = false;
      modeHint.textContent =
        'Editing a booking — save to update. Site is optional until you confirm the day.';
    } else {
      modeHint.hidden = true;
    }
    await refreshLists();
    workTypeSelect.value = job.work_type_id;
    clientSelect.value = job.client_id;
    dateInput.value = job.work_date;
    selectedSiteIds = [...(job.site_ids ?? [])];
    billableInput.checked = job.billable !== false;
    (root.querySelector('#po_number') as HTMLInputElement).value =
      job.po_number ?? '';
    (root.querySelector('#site_reference') as HTMLInputElement).value =
      job.site_reference ?? '';
    (root.querySelector('#notes') as HTMLTextAreaElement).value = job.notes ?? '';
    descriptionInput.value = job.notes ?? '';
    if (job.duration_type === 'custom' && job.custom_amount != null) {
      setDuration('custom');
      customMode = 'amount';
      hoursField.hidden = true;
      amountField.hidden = false;
      amountInput.value = String(job.custom_amount);
      directAmountInput.value = String(job.custom_amount);
      root.querySelectorAll('[data-custom]').forEach((el) => {
        const btn = el as HTMLButtonElement;
        btn.setAttribute(
          'aria-pressed',
          String(btn.dataset.custom === 'amount'),
        );
      });
    } else if (job.duration_type === 'custom' && job.custom_hours != null) {
      setDuration('custom');
      customMode = 'hours';
      hoursField.hidden = false;
      amountField.hidden = true;
      hoursInput.value = String(job.custom_hours);
      root.querySelectorAll('[data-custom]').forEach((el) => {
        const btn = el as HTMLButtonElement;
        btn.setAttribute(
          'aria-pressed',
          String(btn.dataset.custom === 'hours'),
        );
      });
    } else {
      setDuration(job.duration_type);
    }
    applyKindUi();
    renderSites();
    updateSitesHint();
    flash.textContent = '';
    flash.classList.remove('error');
  }

  function clearEdit() {
    editingJob = null;
    submitBtn.textContent = 'Log job';
  }

  await refreshLists();
  updateSitesHint();
  return {
    refreshLists,
    setWorkDate: (iso: string) => {
      dateInput.value = iso;
    },
    setMode: (mode: FormMode) => {
      if (!editingJob) {
        formMode = mode;
        const modeHint = root.querySelector('#form-mode-hint') as HTMLElement;
        if (mode === 'planned') {
          modeHint.hidden = false;
          modeHint.textContent =
            'Saves as provisional. Confirm the day on the calendar when the work was done. Site is optional until then.';
        } else {
          modeHint.hidden = true;
        }
        updateSitesHint();
      }
    },
    loadJob,
    clearEdit,
    focus: () => {
      workTypeSelect.focus();
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
